/**
 * On phones, reveals the icon grid one row at a time: each row slides up from the bottom
 * and fades in as it scrolls into view. Rows are read from the items' positions, so it
 * follows the number of columns in the CSS. Does nothing on larger screens. With reduced
 * motion the rows fade in without moving (see the CSS).
 */
class AvalonIconGrid extends HTMLElement {
  #observer = null;
  #mobileQuery = window.matchMedia('(max-width: 749px)');
  #rows = new Map();

  connectedCallback() {
    this.#mobileQuery.addEventListener('change', this.#setup);
    window.addEventListener('resize', this.#onResize, { passive: true });
    this.#setup();
  }

  disconnectedCallback() {
    this.#mobileQuery.removeEventListener('change', this.#setup);
    window.removeEventListener('resize', this.#onResize);
    this.#teardown();
  }

  get #enabled() {
    return this.#mobileQuery.matches;
  }

  #setup = () => {
    this.#teardown();
    if (!this.#enabled) return;

    const items = Array.from(this.querySelectorAll('.avalon-icon-grid__item'));
    if (items.length === 0) return;

    this.#rows = new Map();
    let rowTop = null;
    let rowIndex = -1;
    let column = 0;

    items.forEach((item) => {
      const top = item.offsetTop;
      if (rowTop === null || Math.abs(top - rowTop) > 4) {
        rowTop = top;
        rowIndex += 1;
        column = 0;
      }
      item.style.setProperty('--avalon-reveal-delay', `${column * 90}ms`);
      column += 1;
      if (!this.#rows.has(rowIndex)) this.#rows.set(rowIndex, []);
      this.#rows.get(rowIndex).push(item);
    });

    this.classList.add('is-reveal-ready');

    this.#observer = new IntersectionObserver(this.#onIntersect, {
      rootMargin: '0px 0px -12% 0px',
    });
    this.#rows.forEach((rowItems) => this.#observer.observe(rowItems[0]));
  };

  #teardown() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.classList.remove('is-reveal-ready');
    this.querySelectorAll('.avalon-icon-grid__item').forEach((item) => {
      item.classList.remove('is-visible');
      item.style.removeProperty('--avalon-reveal-delay');
    });
  }

  #onIntersect = (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      this.#observer.unobserve(entry.target);
      this.#rows.forEach((rowItems) => {
        if (rowItems[0] === entry.target) rowItems.forEach((item) => item.classList.add('is-visible'));
      });
    });
  };

  #resizeFrame = 0;

  #onResize = () => {
    if (!this.#enabled || this.#resizeFrame) return;

    this.#resizeFrame = requestAnimationFrame(() => {
      this.#resizeFrame = 0;
      this.#setup();
    });
  };
}

if (!customElements.get('avalon-icon-grid')) {
  customElements.define('avalon-icon-grid', AvalonIconGrid);
}
